import React, { useState, useEffect, useCallback } from 'react';
import { userService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { DotLoader } from 'react-spinners';
import './UserManagement.css';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const { user: currentUser, isAppManager, canManageUsers } = useAuth();

  // Modal states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState('');

  // Form states for change password
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Form states for add user
  const [newUsername, setNewUsername] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [newUserConfirmPassword, setNewUserConfirmPassword] = useState('');
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserRole, setNewUserRole] = useState('user');
  const [showUserPassword, setShowUserPassword] = useState(false);
  const [showUserConfirmPassword, setShowUserConfirmPassword] = useState(false);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await userService.getAll();
      setUsers(response.users || []);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch users:', err);
      setError('Không thể tải danh sách người dùng');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Auto-hide success toast after 3 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  // Check if current user can delete target user
  const canDelete = (targetUser) => {
    if (!currentUser) return false;
    if (targetUser.id === currentUser.id) return false; // Can't delete yourself
    if (currentUser.role === 'app-manager') {
      return targetUser.role !== 'app-manager'; // Can delete admin and user
    }
    if (currentUser.role === 'admin') {
      return targetUser.role === 'user'; // Admin can only delete users
    }
    return false;
  };

  // Check if current user can edit target user
  const canEdit = (targetUser) => {
    if (!currentUser) return false;
    if (currentUser.role === 'app-manager') return true;
    if (currentUser.role === 'admin') return targetUser.role === 'user';
    return false;
  };

  const openDeleteModal = (user) => {
    setSelectedUser(user);
    setModalError('');
    setShowDeleteModal(true);
  };

  const openPasswordModal = (user) => {
    setSelectedUser(user);
    setNewPassword('');
    setConfirmPassword('');
    setShowNewPassword(false);
    setShowConfirmPassword(false);
    setModalError('');
    setShowPasswordModal(true);
  };

  const openAddModal = () => {
    setNewUsername('');
    setNewUserPassword('');
    setNewUserConfirmPassword('');
    setNewUserEmail('');
    setNewUserRole('user');
    setShowUserPassword(false);
    setShowUserConfirmPassword(false);
    setModalError('');
    setShowAddModal(true);
  };

  const handleDelete = async () => {
    if (!selectedUser) return;

    setModalLoading(true);
    setModalError('');

    try {
      await userService.delete(selectedUser.id);
      setUsers(prev => prev.filter(u => u.id !== selectedUser.id));
      setShowDeleteModal(false);
      setSelectedUser(null);
      setSuccess('Xóa tài khoản thành công');
    } catch (err) {
      setModalError(err.response?.data?.error || 'Không thể xóa người dùng');
    } finally {
      setModalLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (!selectedUser) return;

    if (newPassword.length < 6) {
      setModalError('Mật khẩu phải có ít nhất 6 ký tự');
      return;
    }

    if (newPassword !== confirmPassword) {
      setModalError('Mật khẩu xác nhận không khớp');
      return;
    }

    setModalLoading(true);
    setModalError('');

    try {
      await userService.changePassword(selectedUser.id, newPassword);
      setShowPasswordModal(false);
      setSelectedUser(null);
      setNewPassword('');
      setConfirmPassword('');
      setSuccess('Đổi mật khẩu thành công');
    } catch (err) {
      setModalError(err.response?.data?.error || 'Không thể đổi mật khẩu');
    } finally {
      setModalLoading(false);
    }
  };

  const handleAddUser = async () => {
    if (!newUsername.trim()) {
      setModalError('Tên đăng nhập không được để trống');
      return;
    }

    if (newUserPassword.length < 6) {
      setModalError('Mật khẩu phải có ít nhất 6 ký tự');
      return;
    }

    if (newUserPassword !== newUserConfirmPassword) {
      setModalError('Mật khẩu xác nhận không khớp');
      return;
    }

    setModalLoading(true);
    setModalError('');

    try {
      const response = await userService.create({
        username: newUsername.trim(),
        password: newUserPassword,
        email: newUserEmail.trim() || null,
        role: newUserRole,
      });
      setUsers(prev => [response.user, ...prev]);
      setShowAddModal(false);
      setSuccess('Tạo tài khoản thành công');
    } catch (err) {
      setModalError(err.response?.data?.error || 'Không thể tạo người dùng');
    } finally {
      setModalLoading(false);
    }
  };

  const getRoleBadgeClass = (role) => {
    switch (role) {
      case 'app-manager': return 'role-app-manager';
      case 'admin': return 'role-admin';
      default: return 'role-user';
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'app-manager': return 'App Manager';
      case 'admin': return 'Admin';
      default: return 'User';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('vi-VN');
  };

  const isLoading = loading || users.length === 0;

  return (
    <div className="user-management-container">
      {isLoading && !error && (
        <div className="um-loading-overlay">
          <DotLoader color="#3b82f6" size={50} />
        </div>
      )}

      {error && (
        <div className="um-toast-container">
          <div className="toast error">
            <span className="toast-message">{error}</span>
            <button className="toast-action" onClick={fetchUsers}>
              Tải lại
            </button>
            <button className="toast-close" onClick={() => setError(null)}>
              ×
            </button>
          </div>
        </div>
      )}

      {success && (
        <div className="um-toast-container">
          <div className="toast success">
            <span className="toast-message">{success}</span>
            <button className="toast-close" onClick={() => setSuccess(null)}>
              ×
            </button>
          </div>
        </div>
      )}

      <div className="um-header">
        <span className="header-title">Quản lý tài khoản</span>
        {canManageUsers() && (
          <button className="um-add-btn" onClick={openAddModal}>
            + Thêm tài khoản
          </button>
        )}
      </div>

      <div className="um-table-container">
        <table className="um-table">
          <thead>
            <tr>
              <th>Tên đăng nhập</th>
              <th>Email</th>
              <th>Vai trò</th>
              <th>Ngày tạo</th>
              <th>Trạng thái</th>
              <th>Hành động</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id}>
                <td className="um-username">{user.username}</td>
                <td>{user.email || '-'}</td>
                <td>
                  <span className={`um-role-badge ${getRoleBadgeClass(user.role)}`}>
                    {getRoleLabel(user.role)}
                  </span>
                </td>
                <td>{formatDate(user.created_at)}</td>
                <td>
                  <span className={`um-status ${user.is_active ? 'active' : 'inactive'}`}>
                    {user.is_active ? 'Hoạt động' : 'Vô hiệu'}
                  </span>
                </td>
                <td className="um-actions">
                  {canEdit(user) && (
                    <button
                      className="um-action-btn edit"
                      onClick={() => openPasswordModal(user)}
                      title="Đổi mật khẩu"
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                    </button>
                  )}
                  {canDelete(user) && (
                    <button
                      className="um-action-btn delete"
                      onClick={() => openDeleteModal(user)}
                      title="Xóa"
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="3,6 5,6 21,6" />
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                      </svg>
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Xác nhận xóa</h3>
              <button className="modal-close" onClick={() => setShowDeleteModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p>Bạn có chắc muốn xóa tài khoản <strong>{selectedUser?.username}</strong>?</p>
              {modalError && <p className="modal-error">{modalError}</p>}
            </div>
            <div className="modal-footer">
              <button className="modal-btn cancel" onClick={() => setShowDeleteModal(false)}>
                Hủy
              </button>
              <button
                className="modal-btn danger"
                onClick={handleDelete}
                disabled={modalLoading}
              >
                {modalLoading ? 'Đang xóa...' : 'Xóa'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {showPasswordModal && (
        <div className="modal-overlay" onClick={() => setShowPasswordModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Đổi mật khẩu cho {selectedUser?.username}</h3>
              <button className="modal-close" onClick={() => setShowPasswordModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="modal-input-group">
                <label>Mật khẩu mới</label>
                <div className="password-wrapper">
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    placeholder="Nhập mật khẩu mới"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    autoFocus
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    tabIndex={-1}
                  >
                    {showNewPassword ? (
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                        <line x1="1" y1="1" x2="23" y2="23"/>
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                        <circle cx="12" cy="12" r="3"/>
                      </svg>
                    )}
                  </button>
                </div>
              </div>
              <div className="modal-input-group">
                <label>Xác nhận mật khẩu</label>
                <div className="password-wrapper">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="Nhập lại mật khẩu"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    tabIndex={-1}
                  >
                    {showConfirmPassword ? (
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                        <line x1="1" y1="1" x2="23" y2="23"/>
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                        <circle cx="12" cy="12" r="3"/>
                      </svg>
                    )}
                  </button>
                </div>
              </div>
              {modalError && <p className="modal-error">{modalError}</p>}
            </div>
            <div className="modal-footer">
              <button className="modal-btn cancel" onClick={() => setShowPasswordModal(false)}>
                Hủy
              </button>
              <button
                className="modal-btn confirm"
                onClick={handleChangePassword}
                disabled={modalLoading || !newPassword || !confirmPassword}
              >
                {modalLoading ? 'Đang lưu...' : 'Xác nhận'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add User Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Thêm tài khoản mới</h3>
              <button className="modal-close" onClick={() => setShowAddModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="modal-input-group">
                <label>Tên đăng nhập *</label>
                <input
                  type="text"
                  placeholder="Nhập tên đăng nhập"
                  value={newUsername}
                  onChange={(e) => setNewUsername(e.target.value)}
                  autoFocus
                />
              </div>
              <div className="modal-input-group">
                <label>Mật khẩu *</label>
                <div className="password-wrapper">
                  <input
                    type={showUserPassword ? 'text' : 'password'}
                    placeholder="Nhập mật khẩu (ít nhất 6 ký tự)"
                    value={newUserPassword}
                    onChange={(e) => setNewUserPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowUserPassword(!showUserPassword)}
                    tabIndex={-1}
                  >
                    {showUserPassword ? (
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                        <line x1="1" y1="1" x2="23" y2="23"/>
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                        <circle cx="12" cy="12" r="3"/>
                      </svg>
                    )}
                  </button>
                </div>
              </div>
              <div className="modal-input-group">
                <label>Xác nhận mật khẩu *</label>
                <div className="password-wrapper">
                  <input
                    type={showUserConfirmPassword ? 'text' : 'password'}
                    placeholder="Nhập lại mật khẩu"
                    value={newUserConfirmPassword}
                    onChange={(e) => setNewUserConfirmPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowUserConfirmPassword(!showUserConfirmPassword)}
                    tabIndex={-1}
                  >
                    {showUserConfirmPassword ? (
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                        <line x1="1" y1="1" x2="23" y2="23"/>
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                        <circle cx="12" cy="12" r="3"/>
                      </svg>
                    )}
                  </button>
                </div>
              </div>
              <div className="modal-input-group">
                <label>Email (không bắt buộc)</label>
                <input
                  type="email"
                  placeholder="Nhập email"
                  value={newUserEmail}
                  onChange={(e) => setNewUserEmail(e.target.value)}
                />
              </div>
              <div className="modal-input-group">
                <label>Vai trò</label>
                <select
                  value={newUserRole}
                  onChange={(e) => setNewUserRole(e.target.value)}
                >
                  <option value="user">User</option>
                  {isAppManager() && <option value="admin">Admin</option>}
                  {isAppManager() && <option value="app-manager">App Manager</option>}
                </select>
              </div>
              {modalError && <p className="modal-error">{modalError}</p>}
            </div>
            <div className="modal-footer">
              <button className="modal-btn cancel" onClick={() => setShowAddModal(false)}>
                Hủy
              </button>
              <button
                className="modal-btn confirm"
                onClick={handleAddUser}
                disabled={modalLoading || !newUsername.trim() || !newUserPassword || !newUserConfirmPassword}
              >
                {modalLoading ? 'Đang tạo...' : 'Tạo tài khoản'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
